import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SharePopover from "../components/SharePopover";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { lightBlue } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { cleanHTML, getImageUrl } from "../utils/utils";
import ProjectLinks from "../components/projects/ProjectLinks";

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const response = await API.get(api_paths.PROJECT.get + `/${id}`);

        setProject(response?.data?.payload || null);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching project details:", err);
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [id]);



  return (
    <Box sx={{ py: 6, mt: 8 }}>
      {loading ? (
        <Container maxWidth="lg">
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
              }}
            >
              Back to Portfolio
            </Button>

            <SharePopover
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={project?.title}
            />
          </Box>

          {/* HERO IMAGE */}
          <Skeleton variant="rounded" height={600} />

          {/* TITLE */}
          <Box mt={5}>
            <Skeleton variant="text" width="10%" height={40} />
            <Skeleton variant="text" width="40%" />
          </Box>

          {/* CLIENT + DURATION */}
          <Grid container spacing={3} mt={5}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          </Grid>

          {/* ABOUT */}
          <Box mt={6}>
            <Skeleton variant="text" width="20%" height={30} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="80%" />
          </Box>

          {/* TECHNOLOGIES */}
          <Box mt={5}>
            <Skeleton variant="text" width="20%" height={30} />
            <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" width={80} height={30} />
              ))}
            </Stack>
          </Box>
        </Container>
      ) : project ? (
        <Container maxWidth="lg">
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Button
              onClick={() => {
                navigate("/", { state: { scrollTo: "portfolio" } });
              }}
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
                typography: "textSm",
              }}
            >
              {t("pages.projects.back_to_portfolio")}
            </Button>

            <SharePopover
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={project?.title}
            />
          </Box>

          {/* HERO IMAGE */}
          <Paper elevation={0} sx={{ borderRadius: 4 }}>
            <Box
              component="img"
              src={getImageUrl(project?.image_path)}
              alt={project?.title}
              sx={{
                width: "100%",
                maxHeight: 600,
                objectFit: "cover",
                borderRadius: 3,
              }}
            />
          </Paper>

          <Box mt={5}>
            <Chip
              label={project?.category?.name}
              size="small"
              sx={{
                backgroundColor: lightBlue[50],
                color: theme.palette.primary.main,
                borderRadius: 2,
                fontWeight: 500,
                p: 1,
                typography: "textXs",
              }}
            />
            <Typography
              variant="h4"
              fontWeight={700}
              mt={1}
              color={theme.palette.text.primary}
              sx={{
                fontFamily: theme.typography.h3.fontFamily,
              }}
            >
              {project?.title}
            </Typography>
          </Box>

          {/* CLIENT + DURATION */}
          <Grid container spacing={3} mt={5}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                <Stack direction="column" alignItems="start">
                  <PersonOutlineOutlinedIcon fontSize="small" />
                  <Typography
                    color="text.secondary"
                    sx={{
                      typography: "textXs",
                      mt: 0.5,
                    }}
                  >
                    Client
                  </Typography>
                </Stack>
                <Typography
                  variant="p"
                  fontWeight={600}
                  sx={{
                    typography: "textSm",
                  }}
                >
                  {project?.client}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                <Stack direction="column" alignItems="start">
                  <AccessTimeOutlinedIcon fontSize="small" />
                  <Typography
                    color="text.secondary"
                    sx={{
                      typography: "textXs",
                      mt: 0.5,
                    }}
                  >
                    Duration
                  </Typography>
                </Stack>
                <Typography
                  variant="p"
                  fontWeight={600}
                  sx={{
                    typography: "textSm",
                  }}
                >
                  {project?.duration}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                <Stack direction="column" alignItems="start">
                  <PlaceOutlinedIcon fontSize="small" />
                  <Typography
                    color="text.secondary"
                    sx={{
                      typography: "textXs",
                      mt: 0.5,
                    }}
                  >
                    Country
                  </Typography>
                </Stack>
                <Typography
                  variant="p"
                  fontWeight={600}
                  sx={{
                    typography: "textSm",
                  }}
                >
                  UK
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
                <Stack direction="column" alignItems="start">
                  <LanguageOutlinedIcon fontSize="small" />
                  <Typography
                    color="text.secondary"
                    sx={{
                      typography: "textXs",
                      mt: 0.5,
                    }}
                  >
                    Domain
                  </Typography>
                </Stack>
                <Typography
                  variant="p"
                  fontWeight={600}
                  sx={{
                    typography: "textSm",
                  }}
                >
                  {project?.domain || "—"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* ABOUT */}
          <Box mt={6}>
            <Typography
              variant="h5"
              fontWeight={600}
              mb={2}
              sx={{
                fontFamily: theme.typography.h3.fontFamily,
                typography: "textXl",
              }}
            >
              About the Project
            </Typography>
            <Box
              color="text.secondary"
              lineHeight={1.8}
              sx={{
                wordBreak: "break-word",       // ← fixes overflow on Image 2
                overflowWrap: "break-word",    // ← extra safety for long words
                whiteSpace: "normal",          // ← ensures text wraps normally
                maxWidth: "100%",              // ← prevents stretching beyond container
                "& p": { mb: 1.5 },           // ← adds breathing room between paragraphs
                "& ul, & ol": { pl: 3, my: 1 },
                "& li": { mb: 0.5 },
                "& a": { color: "primary.main", textDecoration: "underline" },
              }}
              dangerouslySetInnerHTML={{
                __html:cleanHTML( project?.description) || "",
              }}
            />
          </Box>

          {/* TECHNOLOGIES */}
          <Box mt={5}>
            <Typography
              variant="h5"
              fontWeight={600}
              mb={2}
              sx={{
                fontFamily: theme.typography.h3.fontFamily,
                typography: "textXl",
              }}
            >
              Technologies Used
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {project?.technology.map((tech, index) => (
                <Chip
                  key={`${tech}-${index}`}
                  label={tech}
                  sx={{
                    backgroundColor: theme.palette.custom.tagBg,
                    color: theme.palette.custom.tagText,
                    fontWeight: 500,
                    typography: "textXs",
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box mt={5}>
            <Typography
              variant="h5"
              fontWeight={600}
              mb={2}
              sx={{
                fontFamily: theme.typography.h3.fontFamily,
                typography: "textXl",
              }}
            >
              {t("pages.home.portfolio.modal.project_links")}
            </Typography>
            <ProjectLinks
              links={project?.links}
              stackSx={{
                spacing: 1,
                justifyContent: "flex-start",
              }}
              chipSx={{
                "& .MuiChip-icon": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          </Box>

          {/* SCREENSHOTS */}
          <Box mt={6}>
            <Typography variant="h5" fontWeight="bold" mb={3}>
              Project Screenshots
            </Typography>
            <Grid container spacing={3}>
              {project.screenshots.map((img, i) => (
                <Grid key={i} size={{ xs: 12, md: 6 }}>
                  <Box
                    component="img"
                    src={getImageUrl(img.image_path)}
                    alt="screenshot"
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      transition: "0.3s",
                      cursor: "pointer",
                      "&:hover": { transform: "scale(1.02)" },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA SECTION */}
          <Box
            sx={{
              mt: 4,
              p: 6,
              textAlign: "center",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="600"
              sx={{
                typography: "textLg",
              }}
            >
              Interested in a similar project?
            </Typography>

            <Typography color="text.secondary" mt={1} mb={3}>
              Let's discuss how we can bring your ideas to life.
            </Typography>

            <Button
              variant="contained"
              // onClick={() => {
              //   navigate("/", { state: { scrollTo: "contact" } });
              // }}
              onClick={() => {
                navigate("/", {
                  state: {
                    scrollTo: "contact",
                    projectRef: {
                      id: project._id,
                      title: project?.title,
                    },
                  },
                });
              }}
              endIcon={<ArrowForwardIcon />}
              sx={{
                typography: "textSm",
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 500,
                background: theme.palette.custom.buttonGradient,
              }}
            >
              Book a Consultation
            </Button>
          </Box>
        </Container>
      ) : (
        <Container maxWidth="lg">
          {/* HEADER */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Button
              component={Link}
              to="/"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                color: theme.palette.text.secondary,
              }}
            >
              Back to Portfolio
            </Button>
            <SharePopover
              url={typeof window !== "undefined" ? window.location.href : ""}
              title={project?.title}
            />
          </Box>
          {/* BODY */}
          <Typography
            variant="h5"
            color="text.secondary"
            textAlign="center"
            mt={10}
          >
            Something went wrong while fetching the project details. Please try
            again later.
          </Typography>
        </Container>
      )}
    </Box>
  );
};

export default ProjectDetail;
